import React, { useState, useEffect } from 'react';
import { Ic, ICONS } from '../data/icons';
import { Card, CardHeader, Badge, Modal, toast, Button, Input, FormField, Select } from '../components/shared';
import { createEnrollmentToken } from '../api/client';
import { QRCodeSVG } from 'qrcode.react';

export default function QREnrollment({ C, currentUser }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deviceType, setDeviceType] = useState('iphone');
  const [expiresIn, setExpiresIn] = useState(24);
  const [showNewQR, setShowNewQR] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);

  const handleGenerateQR = async () => {
    setLoading(true);
    try {
      const data = await createEnrollmentToken({
        device_type: deviceType,
        expires_in_hours: parseInt(expiresIn)
      });
      setQrData(data);
      toast.success('QR kodu başarıyla oluşturuldu');
      setShowNewQR(true);
    } catch (err) {
      toast.error(err.message || 'QR kodu oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    if (qrData?.token) {
      navigator.clipboard.writeText(qrData.token);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Başlık Banner */}
      <div style={{ background: 'linear-gradient(135deg,rgba(168,85,247,0.12),rgba(139,92,246,0.12))', border: `1px solid rgba(168,85,247,0.2)`, borderRadius: 14, padding: '20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 50, height: 50, borderRadius: 12, background: 'linear-gradient(135deg,#a855f7,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Ic d={ICONS.qrcode} size={24} s='#fff' />
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>MDM Kaydı - QR Kod Yönetimi</div>
          <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Yeni cihazlar için QR kod oluştur ve dağıt</div>
        </div>
      </div>

      {/* QR Oluştur Kartı */}
      <Card C={C}>
        <CardHeader C={C} title='Yeni Enrollment QR Kodu Oluştur' />
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label='Cihaz Tipi' C={C}>
              <Select
                C={C}
                value={deviceType}
                onChange={(e) => setDeviceType(e.target.value)}
                disabled={loading}
              >
                <option value='iphone'>📱 iPhone</option>
                <option value='ipad'>📱 iPad</option>
                <option value='mac'>💻 macOS</option>
                <option value='windows'>🪟 Windows</option>
              </Select>
            </FormField>
            <FormField label='Geçerlilik Süresi' C={C}>
              <Select
                C={C}
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                disabled={loading}
              >
                <option value='1'>1 saat</option>
                <option value='4'>4 saat</option>
                <option value='24'>24 saat</option>
                <option value='72'>3 gün</option>
              </Select>
            </FormField>
          </div>

          <button
            onClick={handleGenerateQR}
            disabled={loading}
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              background: C.accent,
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.2s'
            }}
          >
            <Ic d={ICONS.plus} size={16} s='#fff' />
            {loading ? 'Oluşturuluyor...' : 'QR Kodu Oluştur'}
          </button>
        </div>
      </Card>

      {/* Açık QR Kodlar */}
      {!qrData && (
        <Card C={C}>
          <CardHeader C={C} title='Aktif QR Kodlar' />
          <div style={{ padding: '40px', textAlign: 'center', color: C.textFaint }}>
            <Ic d={ICONS.inbox} size={48} s={C.textFaint} />
            <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600 }}>Henüz QR kodu oluşturulmadı</div>
            <div style={{ fontSize: 12, color: C.textFaint, marginTop: 6 }}>İlk QR kodunu oluşturmak için üsteki kartı kullanın</div>
          </div>
        </Card>
      )}

      {/* QR Kod Görüntüsü Modali */}
      <Modal
        isOpen={showNewQR && !!qrData}
        onClose={() => setShowNewQR(false)}
        title='Cihaz Enrollment QR Kodu'
        type='success'
        C={C}
        wide
        confirmLabel='Kapat'
        onConfirm={() => setShowNewQR(false)}
      >
        {qrData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            {/* QR Kodu */}
            <div style={{ 
              padding: 20, 
              background: C.surface2, 
              borderRadius: 12, 
              border: `2px solid ${C.accent}`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <QRCodeSVG 
                value={`https://mdm.ofkoysu.tech/api/enrollment/macos-profile?token=${qrData.token}`} 
                size={280}
                level='H'
                includeMargin={true}
                backgroundColor='#fff'
                fgColor={C.accent}
              />
            </div>

            {/* Bilgi */}
            <div style={{ width: '100%', padding: '12px 14px', background: C.surface2, borderRadius: 10, fontSize: 13, color: C.textSub }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: C.text }}>Cihaz Tipi:</strong> {deviceType.toUpperCase()}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: C.text }}>Geçerlilik:</strong> {expiresIn} saat
              </div>
              <div>
                <strong style={{ color: C.text }}>Oluşturma Tarihi:</strong> {new Date().toLocaleDateString('tr-TR')}
              </div>
            </div>

            {/* Token Tokeni (Fallback) */}
            <div style={{ width: '100%' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.textFaint, marginBottom: 6 }}>Token (QR Tarama Başarısızsa)</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type='text'
                  value={`https://mdm.ofkoysu.tech/api/enrollment/macos-profile?token=${qrData.token}`}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                    background: C.surface2,
                    color: C.text,
                    fontSize: 12,
                    fontFamily: 'monospace',
                    cursor: 'text'
                  }}
                />
                <button
                  onClick={copyToken}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: `1px solid ${copiedToken ? C.green : C.border}`,
                    background: copiedToken ? 'rgba(34,197,94,0.1)' : 'transparent',
                    color: copiedToken ? C.green : C.textSub,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {copiedToken ? '✓ Kopyalandı' : 'Kopyala'}
                </button>
              </div>
            </div>

            {/* Talimatlar */}
            <div style={{ 
              width: '100%', 
              padding: 12, 
              background: 'rgba(59,130,246,0.1)', 
              border: `1px solid rgba(59,130,246,0.3)`,
              borderRadius: 10, 
              fontSize: 12, 
              color: C.textSub,
              lineHeight: '1.6'
            }}>
              <strong style={{ color: C.accent, display: 'block', marginBottom: 6 }}>📌 Kurulum Talimatları:</strong>
              1. Yeni cihazı açın ve başlangıç ekranını başlatın<br />
              2. Wi-Fi kısmında QR kod tarama bekleyin<br />
              3. Şirket telefonundan bu QR kodunu tarayın<br />
              4. Cihaz otomatik olarak MDM profiline kaydolacak
            </div>
          </div>
        )}
      </Modal>

      {/* iPad Enrollment Section */}
      <Card C={C}>
        <CardHeader C={C} title='iPad Enrollment' />
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>iPad Cihaz Kaydı (.mobileconfig)</div>
          <div style={{ padding:'16px', background:C.surface2, borderRadius:12, marginBottom:16 }}>
            <div style={{ fontSize:13, color:C.textSub, marginBottom:12, lineHeight:1.6 }}>
              iPad cihazınızı manuel olarak MDM'ye kaydetmek için aşağıdaki butonla kayıt profilini (.mobileconfig) indirin ve iPad'inizde açın.
            </div>
            <button
              onClick={() => window.open((process.env.REACT_APP_API_URL || 'http://localhost:4000') + '/api/enrollment/macos-profile', '_blank')}
              style={{ padding:'10px 20px', borderRadius:9, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}
            >
              📥 Kayıt Profili (.mobileconfig) İndir
            </button>
          </div>
          <div style={{ fontSize:12, color:C.textFaint }}>
            Profil yüklendikten sonra cihazınız otomatik olarak MDM'ye kaydolacaktır.
          </div>
        </div>
      </Card>

      {/* macOS Enrollment Section */}
      <Card C={C}>
        <CardHeader C={C} title='macOS Enrollment' />
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>macOS Cihaz Kaydı (.mobileconfig)</div>
          <div style={{ padding:'16px', background:C.surface2, borderRadius:12, marginBottom:16 }}>
            <div style={{ fontSize:13, color:C.textSub, marginBottom:12, lineHeight:1.6 }}>
              macOS cihazınızı manuel olarak MDM'ye kaydetmek için aşağıdaki butonla kayıt profilini (.mobileconfig) indirin ve Mac'inizde açın.
            </div>
            <button
              onClick={() => window.open((process.env.REACT_APP_API_URL || 'http://localhost:4000') + '/api/enrollment/macos-profile', '_blank')}
              style={{ padding:'10px 20px', borderRadius:9, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}
            >
              📥 Kayıt Profili (.mobileconfig) İndir
            </button>
          </div>
          <div style={{ fontSize:12, color:C.textFaint }}>
            Profil yüklendikten sonra cihazınız otomatik olarak MDM'ye kaydolacaktır.
          </div>
        </div>
      </Card>

      {/* Windows Enrollment Section */}
      <Card C={C}>
        <CardHeader C={C} title='Windows Enrollment' />
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>Windows Cihaz Kaydı</div>
          <div style={{ padding:'16px', background:C.surface2, borderRadius:12, marginBottom:16 }}>
            <div style={{ fontSize:13, color:C.textSub, marginBottom:12, lineHeight:1.6 }}>
              Windows cihazınızı MDM'ye kaydetmek için aşağıdaki agent uygulamasını indirip kurun veya kayıt linkini kullanın.
            </div>
            <a
              href="/downloads/odo-mdm-agent-setup.exe"
              style={{ display:'inline-block', padding:'10px 20px', borderRadius:9, background:C.accent, color:'#fff', fontSize:13, fontWeight:700, textDecoration:'none', marginBottom:10 }}
              download
            >
              💻 Agent Uygulamasını İndir (Windows)
            </a>
            <div style={{ fontSize:12, color:C.textFaint, marginTop:8 }}>
              Veya kayıt linki: <span style={{ color:C.accent, fontWeight:600 }}>{`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/windows-enroll`}</span>
            </div>
          </div>
          <div style={{ fontSize:12, color:C.textFaint }}>
            Agent kurulumundan sonra cihazınız otomatik olarak MDM'ye kaydolacaktır.
          </div>
        </div>
      </Card>
    </div>
  );
}
